/** @jsxImportSource @emotion/react */
import { Field } from 'react-final-form';
import * as validations from './validations';

/**
 * If the 'initialValues' for the form do not include a key for a
 * given field, the value will be undefined, then it will have a
 * value after redux is hydrated with data. Going from undefined to
 * defined causes errors, so we want to return an empty string instead.
 */
const defaultFormat = (v) => {
  return v ?? '';
};

interface Props {
  label: string;
  name: string;
  type?: string;
  css?: React.CSSProperties;
  labelCss?: React.CSSProperties;
  required?: boolean;
  validate?: (inputValue: string | number) => any;
  format?: (inputValue: string | number) => any;
  id?: string;
  onChange?: any;
  disabled?: boolean;
}

/**
 *
 * @param label the label for the input
 * @param name name of the input field (also the key for the form values object)
 * @param type (optional) the type of input
 * @param css (optional) css override
 * @param labelCss (optional) css override for the label
 * @param required (optional) whether or not the field should be required with validation errors
 * @param validate (optional) validation function to run the input through
 * @param format (optional) format the retrned value of the field
 */
const Input = ({
  label,
  name,
  type = 'text',
  css = null,
  labelCss = null,
  required = false,
  validate = null,
  format,
  id = '',
  onChange = null,
  disabled = false,
}: Props) => {
  return (
    <Field
      name={name}
      component="input"
      type={type}
      css={{ width: '100%' }}
      // if field required but no validate function passed, use default
      validate={required && !validate ? validations.required : validate}
      format={format || defaultFormat}
    >
      {({ input, meta }) => {
        return (
          <div
            css={{
              minHeight: '72px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              width: '100%',
              padding: `8px 0`,
              ...(css ? { ...css } : {}),
            }}
          >
            <div
              css={{
                fontSize: '15px',
                lineHeight: '16px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <p css={{ marginBottom: '6px', ...(labelCss ? labelCss : {}) }}>
                {label}
              </p>
            </div>

            <input
              disabled={disabled}
              onInput={onChange}
              id={id}
              {...input}
              type="text"
              style={{ fontSize: '16px' }}
              css={{
                backgroundColor: disabled ? '#DDD' : '#FFF',
                height: '27px',
                width: '100%',
                ...((meta.error || meta.submitError) && meta.touched
                  ? { outline: '1px solid #f15d22', background: '#ffe9e0' }
                  : {}),
              }}
            />
            <div
              css={{
                height: '12px',
                lineHeight: '12px',
                margin: '2px',
              }}
            >
              {(meta.error || meta.submitError) && meta.touched ? (
                <p
                  id="error-message-mocha"
                  css={{
                    color: '#f15d22',
                    fontSize: '12px',
                  }}
                >
                  {meta.error || meta.submitError}
                </p>
              ) : null}
            </div>
          </div>
        );
      }}
    </Field>
  );
};

export default Input;
